var CourseState_1;
import { __decorate, __metadata } from "tslib";
import { DestroyRef, inject } from '@angular/core';
import { AccessState, AppMessage, PersistStateFactory, takeWhileAlive } from '@core/frontend-shared';
import { AppRequestService, ItemServiceFactory } from '@core/frontend-shared/api';
import { ModelFactoryProvider } from '@core/shared/model';
import { AllExamsProgressRequest, CourseItem, ExamProgressRequest, ExamProgressResponse, ExamRefreshPriority, SceneItem, UpdateSceneProgressRequest, SceneUserProgressItem } from '@mpf/courses/shared';
import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, share, switchMap } from 'rxjs/operators';
import { CourseStateActions } from './course-state.actions';
import * as i0 from "@angular/core";
import * as i1 from "@core/frontend-shared/api";
import * as i2 from "@core/shared/model";
import * as i3 from "@ngxs/store";
import * as i4 from "@core/frontend-shared";
export class CourseProgressModel extends ExamProgressResponse {}
;
const ENABLE_PROGRESS_SAVING = true;
const STORAGE_KEY_LIST_FILTERS = 'course-list-filters';
function orderCoursesWithProgress(data) {
  data.sort((a, b) => {
    const prioA = courseWithProgressToPriorityNumber(a);
    const prioB = courseWithProgressToPriorityNumber(b);
    return prioB - prioA;
  });
  return data;
}
function courseWithProgressToPriorityNumber(data) {
  let prio = 0;
  if (!data.course.isMandatory || !data.progress) {
    if (data.progress?.showAsCompleted) prio -= 10;
    if (data.course.isMandatory) prio += 5;
    if (data.course.highlight) prio += 1;
  } else {
    const ERS = data.progress.examResultStatus;
    if (ERS.refreshPriority === ExamRefreshPriority.URGENT) prio += 15;
    if (ERS.refreshPriority === ExamRefreshPriority.REQUIRED) prio += 10;
    if (data.progress.showAsCompleted) prio -= 10;
    if (data.course.isMandatory) prio += 2;
    if (data.course.highlight) prio += 1;
  }
  return prio;
}
let CourseState = /*#__PURE__*/(() => {
  let CourseState = class CourseState {
    static {
      CourseState_1 = this;
    }
    /* ==== Course List  ====================== */
    static canAccessCourses(hasAbility) {
      return hasAbility;
    }
    static initialized(state) {
      return state.initialized;
    }
    static courses(state) {
      return state.courses;
    }
    static overviewDataIsReady(state) {
      return state.coursesLoadState && state.progressLoadState;
    }
    static coursesWithProgressRaw(state) {
      const coursesWithProgress = state.courses.map(course => {
        const progress = state.userProgress.find(entry => entry.courseId === course.id);
        return {
          course,
          progress
        };
      });
      return coursesWithProgress;
    }
    static tags(state) {
      const tags = [];
      state.courses.forEach(course => {
        course.tags.forEach(tag => {
          if (!tags.find(t => t.id === tag.id)) {
            tags.push(tag);
          }
        });
      });
      return tags;
    }
    static coursesWithProgressOrdered(coursesWithProgress) {
      return orderCoursesWithProgress([...coursesWithProgress]);
    }
    static coursesWithProgressFiltered(filters, canAccessCourses, coursesWithProgress) {
      // if user is not logged in, filter toolbar is not visible and filtering would lead to weird results so skip filtering!
      let courses = [...coursesWithProgress];
      if (canAccessCourses) {
        if (filters.mandatory) {
          courses = courses.filter(c => {
            return c.course.isMandatory;
          });
        }
        if (filters.tags) {
          courses = courses.filter(c => {
            return c.course.tags.find(t => t.id === filters.tags);
          });
        }
        if (filters.name) {
          courses = courses.filter(c => {
            return c.course.name.toLowerCase().includes(filters.name.toLowerCase());
          });
        }
      }
      orderCoursesWithProgress(courses);
      return courses;
    }
    static courseCategoriesWithProgress(coursesWithProgressFiltered) {
      const courses = coursesWithProgressFiltered;
      const categorizedItems = new Map();
      let categories = courses.reduce((catCollection, item) => {
        let existingIndex = 0; // default to index 0 = unsorted items
        if (item.course.category) {
          existingIndex = catCollection.findIndex(match => match.id === item.course.category.id);
          if (existingIndex < 0) {
            catCollection.push(item.course.category);
            existingIndex = catCollection.length - 1;
          }
        }
        const cat = catCollection[existingIndex];
        const itemList = categorizedItems.get(cat.id) || [];
        categorizedItems.set(cat.id, [...itemList, item]);
        return catCollection;
      }, [{
        id: 0,
        ordering: 999,
        name: 'Ohne Kategorie'
      }]);
      categories.sort((a, b) => {
        return a.ordering - b.ordering;
      });
      categories = categories.filter(cat => {
        const items = categorizedItems.get(cat.id);
        return items && items.length;
      });
      return {
        categories,
        categorizedItems
      };
    }
    static listFilterState(state) {
      return state.listFilters;
    }
    /* ==== Single Course  ====================== */
    static selectedCourseWithProgress(state) {
      return {
        course: state.selectedCourse,
        progress: state.selectedCourseProgress
      };
    }
    static selectedCourseScenesWithProgress(state) {
      return state.selectedCourseScenesWithProgress;
    }
    static selectedScene(state) {
      return state.selectedScene;
    }
    static selectedSceneIndex(state) {
      if (!state.selectedScene) return null;
      return state.selectedCourseScenesWithProgress.findIndex(sceneWithProgress => sceneWithProgress.scene.id === state.selectedScene.id);
    }
    static selectedSceneProgress(state) {
      return state.selectedSceneProgress;
    }
    // if no scene is selected, it will return the first matching scene
    static sceneAfterSelectedScene(skipCompletedScenes = false) {
      return createSelector([CourseState_1], state => {
        const scenes = state.selectedCourseScenesWithProgress;
        let newIndex = 0;
        if (state.selectedScene) {
          const currentSceneId = state.selectedScene.id;
          const currIndex = scenes.findIndex(sceneWithProg => sceneWithProg.scene.id === currentSceneId);
          newIndex = currIndex + 1;
        }
        if (!scenes[newIndex]) return false;
        if (!skipCompletedScenes) {
          return scenes[newIndex].scene;
        } else {
          while (newIndex < scenes.length) {
            if (!scenes[newIndex].progress || !scenes[newIndex].progress.completed) {
              return scenes[newIndex].scene;
            }
            newIndex++;
          }
          return false;
        }
      });
    }
    // if no scene is selected, it will return false
    static sceneBeforeSelectedScene(skipCompletedScenes = false) {
      return createSelector([CourseState_1], state => {
        if (!state.selectedScene) return false;
        const scenes = state.selectedCourseScenesWithProgress;
        const currentSceneId = state.selectedScene.id;
        const currIndex = scenes.findIndex(sceneWithProg => sceneWithProg.scene.id === currentSceneId);
        let newIndex = currIndex - 1;
        if (!scenes[newIndex]) return false;
        if (!skipCompletedScenes) {
          return scenes[newIndex].scene;
        } else {
          while (newIndex >= 0) {
            if (!scenes[newIndex].progress || !scenes[newIndex].progress.completed) {
              return scenes[newIndex].scene;
            }
            newIndex--;
          }
          return false;
        }
      });
    }
    constructor(itemServiceFactory, modelFactoryProvider, api, store, persistenceFactory) {
      this.modelFactoryProvider = modelFactoryProvider;
      this.api = api;
      this.store = store;
      this.persistenceFactory = persistenceFactory;
      this.destroyRef = inject(DestroyRef);
      this.persistedListFilters = this.persistenceFactory.create(STORAGE_KEY_LIST_FILTERS, true, 'local');
      this.service = itemServiceFactory.useDestroyRef(this.destroyRef).createSimpleItemService(CourseItem).useCrudEndpoint('public');
    }
    initialize(ctx) {
      if (!ctx.getState().initialized) {
        const persistedFilterSettings = this.persistedListFilters.get();
        if (persistedFilterSettings) {
          ctx.dispatch(new CourseStateActions.SetListFilters(persistedFilterSettings));
        }
        this.store.select(AccessState.loggedIn).pipe(takeWhileAlive(this.destroyRef)).subscribe(loggedIn => {
          ctx.dispatch(new CourseStateActions.ReloadProgressAll());
        });
      }
      ctx.dispatch(new CourseStateActions.ReloadCourses());
      ctx.patchState({
        initialized: true
      });
    }
    /* ==== Course List  ====================== */
    reloadProgressAll({
      getState,
      patchState
    }) {
      const loggedIn = AccessState.loggedIn(this.store.snapshot().access);
      if (loggedIn) {
        this.api.sendRequest(AllExamsProgressRequest, {}).subscribe(response => {
          patchState({
            userProgress: response.progress,
            progressLoadState: true
          });
        });
      } else {
        patchState({
          userProgress: [],
          progressLoadState: true
        });
      }
    }
    reloadCourses({
      getState,
      patchState
    }) {
      this.service.read(true, true).subscribe(items => {
        patchState({
          courses: items,
          coursesLoadState: true
          // userProgress:responses.userProgress
        });
      });
    }
    setListFilters({
      getState,
      patchState
    }, {
      settings
    }) {
      const prevFilters = getState().listFilters;
      const listFilters = {
        ...prevFilters,
        ...settings
      };
      patchState({
        listFilters
      });
      this.persistedListFilters.set(listFilters);
    }
    resetListFilters({
      getState,
      patchState
    }) {
      patchState({
        listFilters: {}
      });
      this.persistedListFilters.remove();
    }
    /* ==== Single Course  ====================== */
    selectCourse({
      patchState
    }, {
      id
    }) {
      const requests = forkJoin({
        course: this.service.crud.readOne(id),
        examProgress: this.api.sendRequest(ExamProgressRequest, {
          courseId: id
        }).pipe(
        // api will return an error if no exam was found for the course.
        // map it to null in that case.
        catchError(err => {
          if (err instanceof AppMessage) {
            err.discard();
          }
          return of(null);
        }), share())
      });
      requests.subscribe(({
        course,
        examProgress
      }) => {
        const selectedCourseScenesWithProgress = course.scenes.map((scene, index) => {
          return {
            scene,
            index,
            progress: scene.userProgress?.[0] || null
          };
        });
        console.log('selectedCourse -> selectedCourseScenesWithProgress', selectedCourseScenesWithProgress);
        patchState({
          selectedCourse: course,
          selectedCourseProgress: examProgress,
          selectedCourseScenesWithProgress,
          selectedScene: undefined,
          selectedSceneProgress: undefined
        });
      });
      // return void observable to prevent misuse of a return value
      return requests.pipe(map(() => void 0));
    }
    reloadCourseProgress({
      getState,
      patchState
    }) {
      const selectedCourse = getState().selectedCourse;
      if (!selectedCourse) return throwError(() => new Error("Kursfortschritt kann nicht geladen werden, da kein Kurs gewählt wurde!"));
      const req = this.api.sendRequest(ExamProgressRequest, {
        courseId: selectedCourse.id
      }).pipe(
      // api will return an error if no exam was found for the course.
      // map it to null in that case.
      catchError(err => {
        if (err instanceof AppMessage) {
          err.discard();
        }
        return of(null);
      }));
      req.subscribe(examProgress => {
        patchState({
          selectedCourseProgress: examProgress
        });
      });
      return req.pipe(switchMap(() => of(void 0)));
    }
    clearSelectedCourse({
      patchState
    }) {
      patchState({
        selectedCourse: undefined,
        selectedCourseProgress: undefined,
        selectedCourseScenesWithProgress: [],
        selectedScene: undefined,
        selectedSceneProgress: undefined
      });
    }
    clearSelectedScene({
      patchState
    }) {
      patchState({
        selectedScene: undefined,
        selectedSceneProgress: undefined
      });
    }
    selectScene({
      patchState,
      getState
    }, {
      id
    }) {
      const selectedCourse = getState().selectedCourse;
      if (!selectedCourse) return throwError(() => new Error("Es kann keine Szene gewählt werden, da kein Kurs gewählt wurde!"));
      const sceneToSelect = getState().selectedCourseScenesWithProgress.find(sceneWithProgress => sceneWithProgress.scene.id === id);
      if (!sceneToSelect) return throwError(() => new Error("Szene mit ID " + id + " wurde nicht gefunden!"));
      patchState({
        selectedScene: sceneToSelect.scene,
        selectedSceneProgress: sceneToSelect.progress
      });
      // return void observable to prevent misuse of a return value
      return of(void 0);
    }
    selectNextScene({
      patchState,
      getState
    }, {
      skipCompletedScenes
    }) {
      const state = getState();
      const selectedCourse = state.selectedCourse;
      if (!selectedCourse) return throwError(() => new Error("Es kann keine Szene gewählt werden, da kein Kurs gewählt wurde!"));
      const currentSceneId = state.selectedScene.id;
      const scenes = state.selectedCourseScenesWithProgress;
      const currentSceneIndex = scenes.findIndex(sceneWithProgress => sceneWithProgress.scene.id === currentSceneId);
      if (currentSceneIndex + 1 >= scenes.length) {
        // next scene does not exist
        console.warn('what to do now?');
        return;
      }
      patchState({
        selectedScene: scenes[currentSceneIndex + 1].scene,
        selectedSceneProgress: scenes[currentSceneIndex + 1].progress
      });
    }
    saveCurrentSceneProgress({
      patchState,
      getState,
      dispatch
    }, {
      progress
    }) {
      if (!ENABLE_PROGRESS_SAVING) return;
      const selectedScene = getState().selectedScene;
      const selectedSceneProgress = getState().selectedSceneProgress;
      if (!selectedScene) return throwError(() => new Error("Keine Szene ausgewählt, es kann kein Fortschritt gespeichert werden!"));
      if (typeof progress !== 'number') throw new Error("Received invalid progress value");
      const modelFactory = this.modelFactoryProvider.createModelFactory(UpdateSceneProgressRequest);
      let sceneProgressPayload;
      if (selectedSceneProgress) {
        if (selectedSceneProgress.progress > progress) {
          // saved user progress is greater than the to be saved progress.
          // in this case, we want the greater result to stay unchanged.
          return;
        }
        sceneProgressPayload = modelFactory.patch(selectedSceneProgress, {
          progress: progress
        });
      } else {
        sceneProgressPayload = modelFactory.fromData({
          sceneId: selectedScene.id,
          progress: progress
        });
      }
      this.api.sendRequest(UpdateSceneProgressRequest, sceneProgressPayload).subscribe(response => {
        dispatch(new CourseStateActions.Internal_UpdateSceneProgressFromServer(response.progress));
        if (response.examAdmissionGranted) {
          dispatch(new CourseStateActions.ReloadCourseProgress());
        }
      });
    }
    _updateSceneProgressFromServer({
      patchState,
      getState,
      dispatch
    }, {
      progressItem
    }) {
      if (!ENABLE_PROGRESS_SAVING) return;
      const newScenesWithProgress = [...getState().selectedCourseScenesWithProgress];
      const indexToUpdate = newScenesWithProgress.findIndex(sceneWithProgress => sceneWithProgress.scene.id === progressItem.sceneId);
      if (indexToUpdate < 0) return; // or throw?
      const newSceneWithProgress = {
        scene: newScenesWithProgress[indexToUpdate].scene,
        index: indexToUpdate,
        progress: progressItem
      };
      newScenesWithProgress[indexToUpdate] = newSceneWithProgress;
      console.log('newScenesWithProgress update from server', newScenesWithProgress);
      const patch = {
        selectedCourseScenesWithProgress: newScenesWithProgress
      };
      // if progress update is for currently selected scene, make sure it is updated too
      const selectedScene = getState().selectedScene;
      if (selectedScene && newSceneWithProgress.scene.id === selectedScene.id) {
        patch.selectedSceneProgress = newSceneWithProgress.progress;
      }
      patchState(patch);
    }
    isCompleted(progress) {
      if (progress?.examResultStatus?.lastPassedDate) {
        if (progress.examResultStatus.refreshPriority === ExamRefreshPriority.NONE) return true;
      }
      return false;
    }
    static {
      this.ɵfac = function CourseState_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CourseState)(i0.ɵɵinject(i1.ItemServiceFactory), i0.ɵɵinject(i2.ModelFactoryProvider), i0.ɵɵinject(i1.AppRequestService), i0.ɵɵinject(i3.Store), i0.ɵɵinject(i4.PersistStateFactory));
      };
    }
    static {
      this.ɵprov = /*@__PURE__*/i0.ɵɵdefineInjectable({
        token: CourseState,
        factory: CourseState.ɵfac
      });
    }
  };
  __decorate([Action(CourseStateActions.Initialize), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], CourseState.prototype, "initialize", null);
  __decorate([Action(CourseStateActions.ReloadProgressAll), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], CourseState.prototype, "reloadProgressAll", null);
  __decorate([Action(CourseStateActions.ReloadCourses), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], CourseState.prototype, "reloadCourses", null);
  __decorate([Action(CourseStateActions.SetListFilters), __metadata("design:type", Function), __metadata("design:paramtypes", [Object, CourseStateActions.SetListFilters]), __metadata("design:returntype", void 0)], CourseState.prototype, "setListFilters", null);
  __decorate([Action(CourseStateActions.ResetListFilters), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], CourseState.prototype, "resetListFilters", null);
  __decorate([Action(CourseStateActions.SelectCourse), __metadata("design:type", Function), __metadata("design:paramtypes", [Object, CourseStateActions.SelectCourse]), __metadata("design:returntype", Observable)], CourseState.prototype, "selectCourse", null);
  __decorate([Action(CourseStateActions.ReloadCourseProgress), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", Observable)], CourseState.prototype, "reloadCourseProgress", null);
  __decorate([Action(CourseStateActions.ClearSelectedCourse), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], CourseState.prototype, "clearSelectedCourse", null);
  __decorate([Action(CourseStateActions.ClearSelectedScene), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", void 0)], CourseState.prototype, "clearSelectedScene", null);
  __decorate([Action(CourseStateActions.SelectScene), __metadata("design:type", Function), __metadata("design:paramtypes", [Object, CourseStateActions.SelectScene]), __metadata("design:returntype", Observable)], CourseState.prototype, "selectScene", null);
  __decorate([Action(CourseStateActions.SelectNextScene), __metadata("design:type", Function), __metadata("design:paramtypes", [Object, CourseStateActions.SelectNextScene]), __metadata("design:returntype", void 0)], CourseState.prototype, "selectNextScene", null);
  __decorate([Action(CourseStateActions.SaveCurrentSceneProgress), __metadata("design:type", Function), __metadata("design:paramtypes", [Object, CourseStateActions.SaveCurrentSceneProgress]), __metadata("design:returntype", void 0)], CourseState.prototype, "saveCurrentSceneProgress", null);
  __decorate([Action(CourseStateActions.Internal_UpdateSceneProgressFromServer), __metadata("design:type", Function), __metadata("design:paramtypes", [Object, CourseStateActions.Internal_UpdateSceneProgressFromServer]), __metadata("design:returntype", void 0)], CourseState.prototype, "_updateSceneProgressFromServer", null);
  __decorate([Selector([AccessState.simpleAbilityCheck("Feature.mpf-courses.View")]), __metadata("design:type", Function), __metadata("design:paramtypes", [Boolean]), __metadata("design:returntype", Boolean)], CourseState, "canAccessCourses", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", Boolean)], CourseState, "initialized", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", Array)], CourseState, "courses", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", Boolean)], CourseState, "overviewDataIsReady", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", Array)], CourseState, "coursesWithProgressRaw", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", Array)], CourseState, "tags", null);
  __decorate([Selector([CourseState.coursesWithProgressRaw]), __metadata("design:type", Function), __metadata("design:paramtypes", [Array]), __metadata("design:returntype", Array)], CourseState, "coursesWithProgressOrdered", null);
  __decorate([Selector([CourseState.listFilterState, CourseState.canAccessCourses, CourseState.coursesWithProgressRaw]), __metadata("design:type", Function), __metadata("design:paramtypes", [Object, Boolean, Array]), __metadata("design:returntype", Array)], CourseState, "coursesWithProgressFiltered", null);
  __decorate([Selector([CourseState.coursesWithProgressFiltered]), __metadata("design:type", Function), __metadata("design:paramtypes", [Array]), __metadata("design:returntype", Object)], CourseState, "courseCategoriesWithProgress", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", Object)], CourseState, "listFilterState", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", Object)], CourseState, "selectedCourseWithProgress", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", Array)], CourseState, "selectedCourseScenesWithProgress", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", SceneItem)], CourseState, "selectedScene", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", Number)], CourseState, "selectedSceneIndex", null);
  __decorate([Selector(), __metadata("design:type", Function), __metadata("design:paramtypes", [Object]), __metadata("design:returntype", SceneUserProgressItem)], CourseState, "selectedSceneProgress", null);
  return CourseState;
})();
CourseState = CourseState_1 = __decorate([State({
  name: 'course',
  defaults: {
    initialized: false,
    canAccessCourses: false,
    courses: [],
    userProgress: [],
    selectedCourse: undefined,
    selectedCourseProgress: undefined,
    selectedCourseScenesWithProgress: [],
    selectedScene: undefined,
    selectedSceneProgress: undefined,
    listFilters: {
      mandatory: null
    },
    coursesLoadState: false,
    progressLoadState: false
  }
}), __metadata("design:paramtypes", [ItemServiceFactory, ModelFactoryProvider, AppRequestService, Store, PersistStateFactory])], CourseState);
export { CourseState };